import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Summary } from '../api/client.js';
import { Alert, Card, PageHeader, Spinner } from '../components/ui.js';
import { dateTime, integer } from '../format.js';
import { KIND_LABELS } from './ImportsPage.js';

/**
 * Home: three counters that say how much data is loaded and how fresh it
 * is, each linking to the page that shows the detail.
 */

export function HomePage() {
  const summary = useQuery({
    queryKey: ['summary'],
    queryFn: () => api.get<Summary>('/api/summary'),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Home"
        description="What is loaded, and where to go next. Import reports first; the SKU and restock pages read from them."
      />

      {summary.isPending && <Spinner label="Loading" />}
      {summary.isError && <Alert>{summary.error.message}</Alert>}
      {summary.data && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Counter
            to="/skus"
            label="SKUs"
            value={integer(summary.data.skus.total)}
            detail={`${integer(summary.data.skus.active)} active`}
          />
          <Counter
            to="/restock"
            label="Order lines"
            value={integer(summary.data.orderLines)}
            detail="FBA and merchant, all statuses"
          />
          <Counter
            to="/imports"
            label="Last import"
            value={
              summary.data.lastImport ? dateTime(summary.data.lastImport.uploadedAt) : 'None yet'
            }
            detail={
              summary.data.lastImport
                ? `${KIND_LABELS[summary.data.lastImport.kind]}, ${summary.data.lastImport.status}`
                : 'Start with an All Listings report'
            }
          />
        </div>
      )}
    </div>
  );
}

function Counter({
  to,
  label,
  value,
  detail,
}: {
  to: string;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <Link to={to} className="block">
      <Card className="p-5 hover:bg-sunken">
        <div className="text-xs font-medium text-muted">{label}</div>
        <div className="mt-1 text-2xl font-semibold tracking-tight text-strong">{value}</div>
        <div className="mt-1 text-xs text-muted">{detail}</div>
      </Card>
    </Link>
  );
}
